import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import type { CarouselItem } from "./types";
import {
  Dot,
  DotTrack,
  FilmstripHero,
  FilmstripSlide,
  FilmstripTrack,
  FilmstripViewport,
  HeroMeta,
  HeroTitle,
  NavArrow,
  NavCluster,
  ShowcaseRoot,
  SlideCaption,
  SlideImage,
} from "./cinematicGlassStyles";

type Props = {
  items: CarouselItem[];
  activeIndex: number;
  onSlideChange: (index: number) => void;
};

const AUTOPLAY_MS = 9000;
/** Cumulative wheel delta before changing slide (trackpads emit small steps per event). */
const WHEEL_SLIDE_THRESHOLD = 72;
const WHEEL_ACCUM_DECAY_MS = 220;

export default function CinematicGlassShowcase({
  items,
  activeIndex,
  onSlideChange,
}: Props) {
  const history = useHistory();
  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [showcaseHot, setShowcaseHot] = useState(false);

  const [tx, setTx] = useState(0);
  const [motionMs, setMotionMs] = useState(680);
  const [autoplayPaused, setAutoplayPaused] = useState(false);
  /** Ref (not state) so pointerup sees the same down X on fast taps — state was often still null on mobile. */
  const dragStartXRef = useRef<number | null>(null);
  const rafRef = useRef<number>(0);
  const autoStartRef = useRef<number>(0);
  const wheelAccumRef = useRef(0);
  const wheelDecayRef = useRef<number>(0);
  /** Skip the next slide click after a horizontal drag (viewport swipe). */
  const suppressSlideClickRef = useRef(false);

  const n = items.length;
  const current = items[activeIndex];

  const recenter = useCallback(() => {
    const vp = viewportRef.current;
    const track = trackRef.current;
    if (!vp || !track || n < 1) return;

    const activeEl = track.children[activeIndex] as HTMLElement | undefined;
    if (!activeEl) return;

    /*
     * Use layout geometry (offsetLeft / offsetWidth), not getBoundingClientRect width.
     * Slides use transform: scale() for active vs inactive; rect width mixes scaled sizes
     * with flex gap math and shifts the track — especially on mobile.
     */
    const vw = vp.clientWidth;
    const centerActive = activeEl.offsetLeft + activeEl.offsetWidth / 2;
    const nextTx = vw / 2 - centerActive;
    setTx(nextTx);
  }, [activeIndex, n]);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const sync = () => {
      setMotionMs(mq.matches ? 0 : 680);
    };
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useLayoutEffect(() => {
    recenter();
    const id = requestAnimationFrame(() => recenter());
    return () => cancelAnimationFrame(id);
  }, [recenter, items.length]);

  useEffect(() => {
    const vp = viewportRef.current;
    const tr = trackRef.current;
    if (!vp) return;
    const ro = new ResizeObserver(() => recenter());
    ro.observe(vp);
    if (tr) ro.observe(tr);
    return () => ro.disconnect();
  }, [recenter]);

  const canGoNext = n >= 2 && activeIndex < n - 1;
  const canGoPrev = n >= 2 && activeIndex > 0;

  const goNext = useCallback(() => {
    if (!canGoNext) return;
    onSlideChange(activeIndex + 1);
  }, [canGoNext, activeIndex, onSlideChange]);

  const goPrev = useCallback(() => {
    if (!canGoPrev) return;
    onSlideChange(activeIndex - 1);
  }, [canGoPrev, activeIndex, onSlideChange]);

  /** Trackpad / horizontal wheel: same navigation as drag (non-passive so we can prevent browser scroll). */
  useEffect(() => {
    const root = rootRef.current;
    if (!root || n < 2) return;

    const flushDecay = () => {
      if (wheelDecayRef.current) {
        window.clearTimeout(wheelDecayRef.current);
        wheelDecayRef.current = 0;
      }
    };

    const scheduleDecayReset = () => {
      flushDecay();
      wheelDecayRef.current = window.setTimeout(() => {
        wheelAccumRef.current = 0;
        wheelDecayRef.current = 0;
      }, WHEEL_ACCUM_DECAY_MS);
    };

    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return;

      let dx = e.deltaX;
      if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) dx *= 16;
      else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) dx *= root.clientWidth || 1;

      if (e.shiftKey && Math.abs(e.deltaY) >= Math.abs(e.deltaX)) {
        let dy = e.deltaY;
        if (e.deltaMode === WheelEvent.DOM_DELTA_LINE) dy *= 16;
        else if (e.deltaMode === WheelEvent.DOM_DELTA_PAGE) dy *= root.clientWidth || 1;
        dx += dy;
      }

      const absX = Math.abs(dx);
      const absY = Math.abs(e.deltaY);
      const horizontalDominant = absX >= absY && absX > 0.5;
      const shiftAsHorizontal = e.shiftKey && absY > 0.5 && absX < absY;

      if (!horizontalDominant && !shiftAsHorizontal) {
        return;
      }

      /* Let the trackpad fall through at the ends so it doesn’t feel “stuck”. */
      if (dx > 0 && !canGoNext) return;
      if (dx < 0 && !canGoPrev) return;

      e.preventDefault();
      wheelAccumRef.current += dx;
      scheduleDecayReset();

      if (wheelAccumRef.current >= WHEEL_SLIDE_THRESHOLD) {
        wheelAccumRef.current = 0;
        flushDecay();
        if (canGoNext) goNext();
      } else if (wheelAccumRef.current <= -WHEEL_SLIDE_THRESHOLD) {
        wheelAccumRef.current = 0;
        flushDecay();
        if (canGoPrev) goPrev();
      }
    };

    root.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      root.removeEventListener("wheel", onWheel);
      flushDecay();
    };
  }, [n, goNext, goPrev, canGoNext, canGoPrev]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragStartXRef.current = e.clientX;
  }, []);

  const onPointerUp = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = dragStartXRef.current;
      dragStartXRef.current = null;
      if (start == null) return;
      const dx = e.clientX - start;
      const narrow = typeof window !== "undefined" && window.innerWidth < 576;
      const threshold = narrow ? 32 : 48;
      if (Math.abs(dx) < threshold) return;
      suppressSlideClickRef.current = true;
      if (dx < 0) goNext();
      else goPrev();
    },
    [goNext, goPrev],
  );

  const onSlideActivate = useCallback(
    (item: CarouselItem, index: number) => {
      if (suppressSlideClickRef.current) {
        suppressSlideClickRef.current = false;
        return;
      }
      onSlideChange(index);
      history.push(item.linkTo);
    },
    [history, onSlideChange],
  );

  useEffect(() => {
    const atEnd = n >= 2 && activeIndex >= n - 1;
    if (n < 2 || autoplayPaused || atEnd) {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      return;
    }

    autoStartRef.current = performance.now();
    const tick = (now: number) => {
      const t = now - autoStartRef.current;
      if (t >= AUTOPLAY_MS) {
        goNext();
        autoStartRef.current = now;
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [n, autoplayPaused, activeIndex, goNext]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const inside =
        showcaseHot || !!rootRef.current?.contains(document.activeElement);
      if (!inside) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goPrev();
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        goNext();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goPrev, goNext, showcaseHot]);

  if (!current || n === 0) return null;

  return (
    <ShowcaseRoot
      ref={rootRef}
      onMouseEnter={() => setShowcaseHot(true)}
      onMouseLeave={() => setShowcaseHot(false)}
    >
      <FilmstripViewport
        ref={viewportRef}
        onPointerDown={onPointerDown}
        onPointerUp={onPointerUp}
        onPointerCancel={() => {
          dragStartXRef.current = null;
        }}
        style={{ cursor: n > 1 ? "grab" : "default" }}
        tabIndex={0}
        role="region"
        aria-roledescription="carousel"
        aria-label="Projects"
      >
        <FilmstripTrack ref={trackRef} $tx={tx} $ms={motionMs}>
          {items.map((item, i) => {
            const active = i === activeIndex;
            return (
              <FilmstripSlide
                key={item.id}
                type="button"
                $active={active}
                aria-current={active ? "true" : undefined}
                aria-label={`Open project: ${item.name}${active ? ", current" : ""}`}
                onClick={() => onSlideActivate(item, i)}
              >
                <SlideImage src={item.src} alt="" decoding="async" draggable={false} />
                <SlideCaption $active={active}>{item.name}</SlideCaption>
              </FilmstripSlide>
            );
          })}
        </FilmstripTrack>
      </FilmstripViewport>

      <FilmstripHero>
        <HeroMeta>
          Center focus · {activeIndex + 1} / {n}
        </HeroMeta>
        <HeroTitle>{current.name}</HeroTitle>
      </FilmstripHero>

      <NavCluster
        onMouseEnter={() => setAutoplayPaused(true)}
        onMouseLeave={() => setAutoplayPaused(false)}
      >
        <NavArrow type="button" aria-label="Previous project" $dir="prev" disabled={!canGoPrev} onClick={goPrev}>
          ‹
        </NavArrow>
        <DotTrack>
          {items.map((_, i) => (
            <Dot
              key={i}
              type="button"
              aria-label={`Go to project ${i + 1}`}
              $active={i === activeIndex}
              onClick={() => onSlideChange(i)}
            />
          ))}
        </DotTrack>
        <NavArrow type="button" aria-label="Next project" $dir="next" disabled={!canGoNext} onClick={goNext}>
          ›
        </NavArrow>
      </NavCluster>
    </ShowcaseRoot>
  );
}
